import type { TFunction } from 'i18next';
import { toUserMessage } from '@/lib/transient';
import type { StatusTone } from '@/components/StatusBadge';
import type { FleetErrorCode, GpsPosition, LoadStatus, Truck, TrucksView } from './api';

/** The Intl locale of a UI language: Serbian stays in Latin script, as the rest of the UI */
export const intlLocale = (language: string): string =>
  language === 'sr' || (language.startsWith('sr-') && !language.includes('Cyrl')) ? 'sr-Latn' : language;

/** "980 km" in the UI language; one decimal under 10 km */
export const formatKm = (metres: number, locale: string): string =>
  `${new Intl.NumberFormat(intlLocale(locale), { maximumFractionDigits: metres < 10_000 ? 1 : 0 }).format(metres / 1000)} km`;

/**
 * A price field's text as a number: '' is no price (null), a number with at most two decimals
 * ('.' or ',') is itself, anything else is invalid (undefined)
 */
export function parsePrice(text: string): number | null | undefined {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  if (!/^\d+([.,]\d{1,2})?$/.test(trimmed)) return undefined;
  return Number(trimmed.replace(',', '.'));
}

/** "5 minutes ago", in the UI language; null for null */
export function lastSeen(iso: string | null, now: Date, locale: string): string | null {
  if (!iso) return null;
  const seconds = Math.round((Date.parse(iso) - now.getTime()) / 1000);
  const format = new Intl.RelativeTimeFormat(intlLocale(locale), { numeric: 'auto' });
  const size = Math.abs(seconds);
  if (size < 60) return format.format(seconds, 'second');
  if (size < 3600) return format.format(Math.round(seconds / 60), 'minute');
  if (size < 86_400) return format.format(Math.round(seconds / 3600), 'hour');
  return format.format(Math.round(seconds / 86_400), 'day');
}

/** The states the GPS column names, in GeoPoint's own words (traccar-web's glance strings) */
export type GlanceState = 'driving' | 'idle' | 'contact' | 'parked' | 'pending' | 'resuming';

/** A drive with no new record for this long has lost its signal (traccar-web's deviceGlance.js) */
export const LIVE_WINDOW_MS = 15 * 60_000;

/** A drive names its speed only above this, km/h (deviceGlance.js's MOVE_SPEED_KNOTS): "U vožnji · 0 km/h" at a light would contradict itself */
export const SPEED_SHOWN_ABOVE_KMH = 3;

export interface Glance {
  state: GlanceState;
  /** km/h, while driving above 3 km/h; otherwise null */
  speedKmh: number | null;
  /** How long a parked truck has stood, ms; null when GPS did not say */
  parkedMs: number | null;
}

/**
 * What the GPS column says about a truck, as traccar-web's deviceGlance.js reads the stamp: a drive is
 * believed for 15 minutes; idle, contact, pending and resuming hold until the next record; parked says how
 * long. null: no stamp, a state it does not know, or a drive gone quiet (the column then says online or
 * offline and when the truck was last heard).
 */
export function glanceOf(position: GpsPosition | null | undefined, now: Date): Glance | null {
  const stamp = position?.vehicleState;
  if (!position || !stamp) return null;
  const state = stamp === 'manoeuvre' ? 'driving' : stamp;
  switch (state) {
    case 'driving': {
      if (now.getTime() - Date.parse(position.fixTime) > LIVE_WINDOW_MS) return null;
      const speed = Math.round(position.speedKmh);
      return { state, speedKmh: speed > SPEED_SHOWN_ABOVE_KMH ? speed : null, parkedMs: null };
    }
    case 'idle':
    case 'contact':
    case 'pending':
    case 'resuming':
      return { state, speedKmh: null, parkedMs: null };
    case 'parked': {
      const since = position.parkedSince ? Date.parse(position.parkedSince) : Number.NaN;
      return { state, speedKmh: null, parkedMs: Number.isFinite(since) ? Math.max(0, now.getTime() - since) : null };
    }
    default:
      return null;
  }
}

/** echo takes GPS from the live stream only while it spoke this recently (liveCache.ts's LIVE_FRESH_MS) */
export const LIVE_FEED_MS = 90_000;

/**
 * Whether the Uživo marker shows: the stream is live and spoke less than 90 s ago. echo keeps the state "live"
 * through a quiet stretch, so the state alone is not enough; this is the same rule echo applies to its cache.
 */
export function feedIsLive(feed: TrucksView['feed'], now: Date): boolean {
  return feed?.state === 'live' && feed.lastMessageAt !== null && now.getTime() - Date.parse(feed.lastMessageAt) < LIVE_FEED_MS;
}

/** The banner's i18n key for the list's gpsError; only a super admin is told about the token */
export function gpsBannerKey(error: FleetErrorCode | null, isSuperAdmin: boolean): string | null {
  if (!error) return null;
  if (error === 'gps_not_connected') return 'fleet.gps.notConnected';
  if (error === 'gps_auth' && isSuperAdmin) return 'fleet.gps.auth';
  return 'fleet.gps.unavailable';
}

const GPS_CODES: readonly string[] = ['gps_not_connected', 'gps_unavailable', 'gps_auth'];

/** The API error code of a normalised axios error, if any */
export const errorCode = (error: unknown): string | undefined => (error as { data?: { error?: string } } | null)?.data?.error;

/**
 * GPS failures answer 503, which polaris reads as "server restarting"; say what is really wrong
 * instead. Any other error keeps polaris's wording.
 */
export function fleetErrorMessage(error: unknown, fallback: string, isSuperAdmin: boolean, t: TFunction): string {
  const code = errorCode(error);
  if (code && GPS_CODES.includes(code)) return t(gpsBannerKey(code as FleetErrorCode, isSuperAdmin)!);
  return toUserMessage(error, fallback);
}

/** The name damis keeps in GPS, the same rule as echo's trucks.ts */
export const traccarName = (truck: Pick<Truck, 'plate' | 'model'>): string =>
  truck.model ? `${truck.plate} - ${truck.model}` : truck.plate;

/** L-1024 */
export const loadNumber = (number: number): string => `L-${number}`;

/** €1,250.50 in the UI language */
export const formatMoney = (value: number, locale: string): string =>
  new Intl.NumberFormat(intlLocale(locale), { style: 'currency', currency: 'EUR' }).format(value);

/** "13 Oct, 08:30" in the browser's zone and the UI language */
export const formatDateTime = (iso: string, locale: string): string =>
  new Intl.DateTimeFormat(intlLocale(locale), { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));

/** "14:20" in the UI language's clock */
export const formatTime = (date: Date, locale: string): string =>
  new Intl.DateTimeFormat(intlLocale(locale), { hour: '2-digit', minute: '2-digit' }).format(date);

/** A load's length: "45min", "12h 30min", "2d 6h", the same in every language (Serbian's narrow hour is "č") */
export function formatDuration(ms: number): string {
  const minutes = Math.max(0, Math.round(ms / 60_000));
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const rest = minutes % 60;
  if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  if (hours > 0) return rest > 0 ? `${hours}h ${rest}min` : `${hours}h`;
  return `${rest}min`;
}

/** A load's window: "08:00–20:00" within a day, "uto 08:00 → čet 14:00" across days */
export function formatWindow(fromIso: string, toIso: string, locale: string): string {
  const from = new Date(fromIso);
  const to = new Date(toIso);
  if (from.toDateString() === to.toDateString()) return `${formatTime(from, locale)}–${formatTime(to, locale)}`;
  const weekday = new Intl.DateTimeFormat(intlLocale(locale), { weekday: 'short' });
  return `${weekday.format(from)} ${formatTime(from, locale)} → ${weekday.format(to)} ${formatTime(to, locale)}`;
}

/** "uto 08:00": a weekday and a time, for a stop line */
export function formatDayTime(iso: string, locale: string): string {
  const date = new Date(iso);
  return `${new Intl.DateTimeFormat(intlLocale(locale), { weekday: 'short' }).format(date)} ${formatTime(date, locale)}`;
}

const pad = (value: number) => String(value).padStart(2, '0');

/** An ISO time as a datetime-local input's value, in the browser's zone */
export function toLocalInput(iso: string): string {
  const date = new Date(iso);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** A datetime-local input's value (browser zone) as ISO UTC; '' or a broken value is null */
export function fromLocalInput(value: string): string | null {
  if (!value) return null;
  const date = new Date(value); // "YYYY-MM-DDTHH:mm" without a zone is local time
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/** A date input's day ("YYYY-MM-DD") as its local midnight in ISO; `days` later for an exclusive end */
export function dayStart(date: string, days = 0): string {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(year, month - 1, day + days).toISOString();
}

const INVITE_LANGUAGES = ['en', 'es', 'sr'];

/** The invitation's language: the UI language when echo has it, English otherwise */
export function inviteLanguage(language: string): string {
  const short = language.slice(0, 2);
  return INVITE_LANGUAGES.includes(short) ? short : 'en';
}

/** A picker's items: the active ones, plus the current choice even when it was switched off since */
export function activeOrCurrent<T extends { id: string; active: boolean }>(items: T[], currentId: string | null): T[] {
  return items.filter((item) => item.active || item.id === currentId);
}

/** The badge tone of each load status */
export const LOAD_TONE: Record<LoadStatus, StatusTone> = {
  planned: 'info',
  in_transit: 'transit',
  delivered: 'success',
  cancelled: 'neutral',
};

/**
 * A place's town, for tight spots like the planner's bars and cards: "Company, Street, Town, Country" gives the
 * town (the part before the country); "Town, Country" gives the town; a bare name stays as it is
 */
export function townOf(place: string): string {
  const parts = place.split(',').map((part) => part.trim()).filter(Boolean);
  if (parts.length <= 1) return place.trim();
  return parts.length === 2 ? parts[0] : parts[parts.length - 2];
}

